import type { StringObject } from "./StringObject";

export type MirrorEasingFn = (value: number) => number;

/**
 * Lightweight wrapper that mirrors a primary StringObject while keeping
 * its own easing and state. Intended for elements linked via
 * `[string-copy-from]`.
 */
export class StringMirrorObject {
  public readonly id: string;
  public readonly htmlElement: HTMLElement;

  private properties = new Map<string, any>();
  private easingFn?: MirrorEasingFn;

  constructor(id: string, element: HTMLElement, private parent: StringObject) {
    this.id = id;
    this.htmlElement = element;
  }

  public get parentObject(): StringObject {
    return this.parent;
  }

  public setProperty<T>(key: string, value: T): void {
    this.properties.set(key, value);
  }

  public getProperty<T>(key: string): T {
    return this.properties.get(key) ?? null;
  }

  public setEasing(easing: MirrorEasingFn | null | undefined): void {
    this.easingFn = easing ?? undefined;
  }

  public getEasing(): MirrorEasingFn | undefined {
    return this.easingFn;
  }

  /**
   * Returns eased progress using mirror easing (if set) or fallback.
   */
  public applyProgress(rawProgress: number, fallback?: MirrorEasingFn): number {
    const easing = this.easingFn ?? fallback;
    return easing ? easing(rawProgress) : rawProgress;
  }
}
